import type { ReliabilityTier, VisibilityScore } from '../types/index.js';
import { ENGINE_LABELS } from '../types/index.js';
import { ConfidenceBadge } from './ConfidenceBadge.js';
import { MeasureBar } from './ui/MeasureBar.js';

/**
 * Reliability tier of a POOLED estimate from its aggregate sample size — mirrors
 * the backend `getReliabilityTier` (src/scoring/visibility.ts). A windowed mention
 * rate over N responses has a CI ∝ 1/√N, so confidence is a function of the pooled
 * N, NOT the worst single bucket: summing thin per-(day×category) buckets into a
 * large window genuinely reduces variance. Spec 179 — replaces the earlier worst-of
 * derivation, which under-claimed (stamped "directional" on n=thousands).
 */
export function reliabilityTierForSampleSize(n: number): ReliabilityTier {
  if (n >= 400) return 'high_confidence';
  if (n >= 50) return 'operational';
  return 'directional';
}

export interface EngineAggregation {
  engine: string;
  mention_rate: number;
  avg_position: number | null;
  avg_sentiment: number | null;
  share_of_voice: number | null;
  sample_size: number;
  // Reliability tier for this engine, derived from the SUMMED sample size by
  // `reliabilityTierForSampleSize` above (spec 179).
  //
  // Spec 214 corrects this comment: it previously described a worst-of
  // derivation across the contributing rows, 'never recomputed from a summed
  // n'. Spec 179 REPLACED that, because worst-of under-claimed — it stamped
  // 'directional' on n in the thousands. The comment had outlived the code it
  // described, and a comment asserting a guarantee the code no longer makes is
  // how the old behaviour gets 'restored' by the next reader.
  reliability: ReliabilityTier;
  // Unprompted: only over queries where the brand name/aliases were NOT in the
  // rendered prompt. Null/0 when every prompt named the brand. The headline UI
  // metric — raw mention_rate is now treated as a less-load-bearing reference
  // figure since it's gameable when prompts are heavily branded.
  unprompted_sample_size: number;
  unprompted_mention_rate: number | null;
  // ── Citation visibility: a SECOND, independent measurement ──
  // The mention rate answers "did the answer text NAME the brand". These fields
  // answer "was the brand's own DOMAIN cited as a source". An engine can lean on
  // a brand heavily while almost never naming it, so neither number substitutes
  // for the other and neither is blended into the other.
  //
  // `grounded_sample_size` is the citation rate's OWN denominator: responses
  // where the engine actually searched the web. It is <= `sample_size`, and the
  // difference is the visible exclusion (an answer written from weights alone
  // could not have cited anything).
  //
  // `brand_citation_rate === null` = NOT EVALUABLE (no web-searched answers).
  // `0` = measured zero. Rendering a null as "0%" would assert a measurement
  // that was never made.
  grounded_sample_size: number;
  brand_retrieved_count: number;
  brand_cited_count: number;
  brand_retrieval_rate: number | null;
  brand_citation_rate: number | null;
  // The SAME citation measurement over the UNPROMPTED population only.
  //
  // This exists because the two numbers on this card must describe the same
  // responses. The headline is `unprompted_mention_rate` whenever it exists —
  // branded prompts EXCLUDED — while `brand_citation_rate` above is over every
  // invoked response, branded prompts INCLUDED. Printing "3% unprompted" next
  // to "70% cited" when the 70% is drawn from a wider population invites a
  // subtraction that isn't valid, and it is biased in this metric's own favour:
  // a prompt that names the brand is exactly the prompt most likely to surface
  // the brand's own domain as a source.
  //
  // Null contract is unchanged: `null` = not evaluable, `0` = measured zero.
  unprompted_grounded_sample_size: number;
  unprompted_brand_cited_count: number;
  unprompted_brand_citation_rate: number | null;
}

interface EngineVisibilityCardProps {
  data: EngineAggregation;
}

/**
 * Spec 234 §5: a light column for the range, an ink dash at the value. These
 * three rows are independent measurements (position/sentiment/SOV), none
 * more privileged than the others, so none is `emphasize`d — the headline
 * mention rate above already carries the card's one accent.
 */
function MetricRow({
  label,
  value,
  fraction,
}: {
  label: string;
  value: string;
  /** 0–1, or null when there is nothing to mark (spec 234 §5: a zero is a
   *  dash on the floor; missing data draws no dash). */
  fraction: number | null;
}) {
  return (
    <div className="space-y-0.5">
      <div className="flex items-center justify-between text-xs">
        <span className="text-slate-500">{label}</span>
        <span className="font-medium text-slate-700">{value}</span>
      </div>
      <MeasureBar fraction={fraction} />
    </div>
  );
}

export function EngineVisibilityCard({ data }: EngineVisibilityCardProps) {
  // Spec 234: the per-engine accent is retired — engine identity is now
  // carried entirely by `label`, in ink, like every other entity in this app.
  const label = ENGINE_LABELS[data.engine] ?? data.engine;

  const mentionPct = Math.round(data.mention_rate * 100);
  // Headline becomes unprompted when we have unprompted samples; otherwise we
  // fall back to raw mention_rate (e.g. brand-new brands with only branded
  // prompts in the library).
  const headlinePct = data.unprompted_mention_rate != null
    ? Math.round(data.unprompted_mention_rate * 100)
    : mentionPct;
  const headlineLabel = data.unprompted_mention_rate != null ? 'unprompted' : 'mentioned';
  const showRawSubtitle = data.unprompted_mention_rate != null && mentionPct !== headlinePct;
  const positionDisplay = data.avg_position != null ? data.avg_position.toFixed(1) : '--';
  const sentimentDisplay = data.avg_sentiment != null
    ? (data.avg_sentiment >= 0 ? '+' : '') + data.avg_sentiment.toFixed(2)
    : '--';
  const sovDisplay = data.share_of_voice != null
    ? `${Math.round(data.share_of_voice * 100)}%`
    : '--';

  // ── Citation visibility (second, independent measurement) ──
  // Three mutually exclusive states, and the distinction between them IS the
  // honesty contract:
  //   null   → not evaluable  (the engine never searched the web here)
  //   0      → labeled zero   (it searched, and never cited this brand)
  //   > 0    → measured rate
  // A null must NEVER render as "0%": that would claim a measurement GEO Observatory
  // did not make.
  //
  // POPULATION MATCHING. The citation number is read over the SAME responses as
  // whichever headline this card is showing. When the headline is the unprompted
  // mention rate, the citation rate must be the unprompted one; when the card
  // falls back to the raw mention rate, both are over every response. Mixing the
  // two would put "3% unprompted" beside a citation rate drawn from a wider set
  // that includes branded prompts — the prompts most likely to surface the
  // brand's own domain — so the comparison would be biased in favour of the
  // very claim this metric exists to make.
  //
  // There is deliberately NO cross-population fallback: if the matched
  // population has no web-searched answers, the answer is "Not evaluable", even
  // when the other population has plenty. Borrowing the other number would
  // reintroduce exactly the mismatch this avoids.
  const usingUnpromptedHeadline = data.unprompted_mention_rate != null;
  const grounded = usingUnpromptedHeadline
    ? data.unprompted_grounded_sample_size
    : data.grounded_sample_size;
  const citationRate = usingUnpromptedHeadline
    ? data.unprompted_brand_citation_rate
    : data.brand_citation_rate;
  const isCitationEvaluable = citationRate != null && grounded > 0;
  const citationPct = citationRate != null ? Math.round(citationRate * 100) : 0;
  // A rate that is genuinely non-zero but rounds to 0 reads as "never cited",
  // which is a different (and false) claim — so it gets its own display.
  const citationDisplay = !isCitationEvaluable
    ? 'Not evaluable'
    : citationRate! > 0 && citationPct === 0
      ? '<1%'
      : `${citationPct}%`;
  // The denominator is responses whose analysis COMPLETED and whose engine
  // searched the web — the score row is only ever computed over completed
  // analyses. Saying just "answers where the engine searched the web" would
  // claim a wider denominator than we actually counted. The population word
  // keeps the citation basis readable against the headline right above it.
  const populationNoun = usingUnpromptedHeadline ? 'unprompted answer' : 'answer';
  const answerNoun = grounded === 1 ? populationNoun : `${populationNoun}s`;
  const citationBasis = !isCitationEvaluable
    ? `No analysed ${populationNoun}s where ${label} searched the web`
    : citationRate === 0
      ? `Never cited in ${grounded} analysed ${answerNoun} where ${label} searched the web`
      : `of the ${grounded} analysed ${answerNoun} where ${label} searched the web`;

  // Directional = thin sample. The `~` says "approximately" so it doesn't read
  // as a validated figure, and the ConfidenceBadge beside the engine name says
  // the tier and n in words. Spec 250 (review P1-5): the figure itself is ink
  // at the figure size — tone lives in the note and badge, never the figure;
  // it was amber-700 at 700, the same move spec 248 made on Overview.
  const isDirectional = data.reliability === 'directional';
  const headlineClass = 'hub-stat__value';

  return (
    // Spec 234: no border on the sheet-radius wrapper — a bordered rounded-lg
    // panel under a dashboard root is exactly the card residue gate 231.A.2
    // exists to catch (rounded-lg is 14px here, inside that band).
    <div className="rounded-lg bg-white overflow-hidden" data-testid="engine-visibility-card">
      <div className="h-[3px] bg-[var(--hub-color-ink)]" />
      <div className="space-y-3 p-5">
        {/* Header */}
        <div className="flex items-center justify-between">
          <span className="text-sm font-semibold text-slate-800">{label}</span>
          <ConfidenceBadge reliability={data.reliability} sampleSize={data.sample_size} />
        </div>

        {/* Large mention rate (unprompted is the load-bearing number) */}
        <div>
          <span className={headlineClass} data-testid="engine-mention-rate">
            {isDirectional ? '~' : ''}{headlinePct}%
          </span>
          <span className="ml-1 text-sm text-slate-500">{headlineLabel}</span>
          {showRawSubtitle && (
            <div className="text-xs text-slate-400">
              Raw mention rate: {mentionPct}% (incl. branded prompts)
            </div>
          )}
        </div>

        {/* Metric rows */}
        <div className="space-y-3">
          <MetricRow
            label="Avg position"
            value={positionDisplay}
            fraction={data.avg_position != null ? Math.max(0, 10 - data.avg_position) / 10 : null}
          />
          <MetricRow
            label="Sentiment"
            value={sentimentDisplay}
            fraction={data.avg_sentiment != null ? (data.avg_sentiment + 1) / 2 : null}
          />
          <MetricRow
            label="SOV"
            value={sovDisplay}
            fraction={data.share_of_voice}
          />
        </div>

        {/*
          Second measurement — deliberately set apart by its own rule so it does
          not read as a fourth attribute of the mention rate above it. It has its
          own denominator (answers where the engine actually searched the web),
          so it carries its own sample size and its own confidence badge; reusing
          the mention rate's badge here would attach confidence earned on one
          sample to a number computed over a different, smaller one.
        */}
        <div className="border-t border-slate-100 pt-3">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              <div className="text-xs text-slate-500">Cited as a source</div>
              <div
                data-testid="engine-citation-rate"
                className={
                  isCitationEvaluable
                    ? 'text-lg font-semibold leading-tight text-slate-800'
                    : 'text-sm font-medium leading-tight text-slate-400'
                }
              >
                {citationDisplay}
              </div>
            </div>
            {grounded > 0 && (
              <ConfidenceBadge
                reliability={reliabilityTierForSampleSize(grounded)}
                sampleSize={grounded}
              />
            )}
          </div>
          <div data-testid="engine-citation-basis" className="mt-1 text-xs text-slate-400">
            {citationBasis}
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * Aggregate per-engine scores across categories into a single EngineAggregation.
 */
/**
 * Canonical engine order. Exported (spec 220) so a PAUSED engine — which has
 * no scores and therefore no aggregation — can be rendered in its usual
 * position rather than appended after the engines that do have data.
 */
export const ENGINE_ORDER: readonly string[] = ['chatgpt', 'gemini', 'claude', 'google_aio', 'mistral'];

export function aggregateByEngine(scores: VisibilityScore[]): EngineAggregation[] {
  const byEngine = new Map<string, VisibilityScore[]>();
  for (const s of scores) {
    const list = byEngine.get(s.engine) ?? [];
    list.push(s);
    byEngine.set(s.engine, list);
  }

  const engineOrder = ENGINE_ORDER;
  const result: EngineAggregation[] = [];

  // Sample-weighted mean of a metric across rows: Σ(value_i × sample_size_i) /
  // Σ(sample_size_i) over rows where the value is non-null. When `history` feeds
  // this (many day×category rows per engine) a flat mean over-counts thin days;
  // weighting by sample_size matches the trend chart's sample-weighted line.
  // Guards divide-by-zero → null.
  const weightedAvg = (
    items: VisibilityScore[],
    pick: (i: VisibilityScore) => number | null,
  ): number | null => {
    let weightedSum = 0;
    let totalWeight = 0;
    for (const i of items) {
      const v = pick(i);
      if (v == null) continue;
      const w = i.sample_size ?? 0;
      if (w <= 0) continue;
      weightedSum += v * w;
      totalWeight += w;
    }
    return totalWeight > 0 ? weightedSum / totalWeight : null;
  };

  const buildAggregation = (engine: string, items: VisibilityScore[]): EngineAggregation => {
    // Sample-weighted average of unprompted_mention_rate so engines with few
    // unprompted queries don't drown out the engines doing real measurement.
    const totalUnpromptedSamples = items.reduce((sum, i) => sum + (i.unprompted_sample_size ?? 0), 0);
    const totalUnpromptedMentions = items.reduce(
      (sum, i) => sum + (i.unprompted_mentioned_count ?? 0),
      0,
    );
    const unpromptedRate = totalUnpromptedSamples > 0
      ? totalUnpromptedMentions / totalUnpromptedSamples
      : null;

    const sampleSize = items.reduce((sum, i) => sum + i.sample_size, 0);

    // Citation visibility over the window. SUM the counts and derive the rate
    // from the summed totals — never average the per-row rates. This is exactly
    // what unprompted_* does above, and it is what keeps the windowed citation
    // number consistent with the windowed mention number: a flat mean over rows
    // would let a 1-response day weigh as much as a 200-response day, and a
    // thin day whose rate is null would silently vanish from the denominator.
    const groundedSampleSize = items.reduce((sum, i) => sum + (i.grounded_sample_size ?? 0), 0);
    const citedCount = items.reduce((sum, i) => sum + (i.brand_cited_count ?? 0), 0);
    const retrievedCount = items.reduce((sum, i) => sum + (i.brand_retrieved_count ?? 0), 0);
    // Zero grounded answers ⇒ null (not evaluable), never 0 (measured zero).
    const citationRate = groundedSampleSize > 0 ? citedCount / groundedSampleSize : null;
    const retrievalRate = groundedSampleSize > 0 ? retrievedCount / groundedSampleSize : null;

    // The same window sum over the UNPROMPTED population — summed counts, rate
    // derived from the totals, exactly as above and exactly as unprompted_*
    // does for the mention rate. The card picks whichever of the two pairs
    // matches the headline it renders, so both must be aggregated identically
    // or the choice between them would introduce its own skew.
    const unpromptedGrounded = items.reduce(
      (sum, i) => sum + (i.unprompted_grounded_sample_size ?? 0),
      0,
    );
    const unpromptedCited = items.reduce(
      (sum, i) => sum + (i.unprompted_brand_cited_count ?? 0),
      0,
    );
    const unpromptedCitationRate = unpromptedGrounded > 0
      ? unpromptedCited / unpromptedGrounded
      : null;

    return {
      engine,
      mention_rate: weightedAvg(items, (i) => i.mention_rate) ?? 0,
      avg_position: weightedAvg(items, (i) => i.avg_position),
      avg_sentiment: weightedAvg(items, (i) => i.avg_sentiment),
      share_of_voice: weightedAvg(items, (i) => i.share_of_voice),
      sample_size: sampleSize,
      // Spec 179: tier from the POOLED window sample size (this engine's total
      // responses), not worst-of the thin per-day buckets. A windowed estimate
      // over a large N is genuinely high-confidence; worst-of under-claimed it.
      reliability: reliabilityTierForSampleSize(sampleSize),
      unprompted_sample_size: totalUnpromptedSamples,
      unprompted_mention_rate: unpromptedRate,
      grounded_sample_size: groundedSampleSize,
      brand_retrieved_count: retrievedCount,
      brand_cited_count: citedCount,
      brand_retrieval_rate: retrievalRate,
      brand_citation_rate: citationRate,
      unprompted_grounded_sample_size: unpromptedGrounded,
      unprompted_brand_cited_count: unpromptedCited,
      unprompted_brand_citation_rate: unpromptedCitationRate,
    };
  };

  for (const engine of engineOrder) {
    const items = byEngine.get(engine);
    if (!items || items.length === 0) continue;
    result.push(buildAggregation(engine, items));
  }

  // Include engines not in the standard order
  for (const [engine, items] of byEngine) {
    if (engineOrder.includes(engine)) continue;
    result.push(buildAggregation(engine, items));
  }

  return result;
}
